import React, { useState, useEffect, useCallback } from 'react';
import { authApi } from '../api/client';
import { useAuth } from '../context/AuthContext';

export default function AdminPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.is_super_admin;

  const [invites, setInvites] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  // Form state
  const [grantsAdmin, setGrantsAdmin] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState('');
  const [createdToken, setCreatedToken] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [invitesRes, usersRes] = await Promise.all([
        authApi.listInvites(),
        authApi.listUsers(),
      ]);
      setInvites(invitesRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setCreatedToken(null);
    setCopied(false);

    try {
      const data = { grants_admin: grantsAdmin };
      if (expiresInDays) {
        data.expires_in_days = parseInt(expiresInDays, 10);
      }
      const res = await authApi.createInvite(data);
      setCreatedToken(res.data.token);
      setGrantsAdmin(false);
      setExpiresInDays('');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create invite token');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This will also delete their alerts and notifications.`)) {
      return;
    }
    setError('');
    try {
      await authApi.deleteUser(userId);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getTokenStatus = (invite) => {
    if (invite.used_by) return 'used';
    if (invite.expires_at && new Date(invite.expires_at) < new Date()) return 'expired';
    return 'available';
  };

  const getTokenRole = (invite) => {
    if (invite.grants_super_admin) return 'Super Admin';
    if (invite.grants_admin) return 'Admin';
    return 'User';
  };

  const getTokenRoleClass = (invite) => {
    if (invite.grants_super_admin) return 'role-super-admin';
    if (invite.grants_admin) return 'role-admin';
    return 'role-user';
  };

  const getUserRole = (u) => {
    if (u.is_super_admin) return 'Super Admin';
    if (u.is_admin) return 'Admin';
    return 'User';
  };

  const getUserRoleClass = (u) => {
    if (u.is_super_admin) return 'role-super-admin';
    if (u.is_admin) return 'role-admin';
    return 'role-user';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="admin-page">
      <h2>Admin Panel</h2>
      <p className="page-description">
        {isSuperAdmin
          ? 'Super Admin — Generate admin or regular user invite tokens. Manage all users.'
          : 'Admin — Generate regular user invite tokens. Manage users you invited.'}
      </p>

      {error && <div className="error-message">{error}</div>}

      {/* Create Token Form */}
      <div className="admin-create-section">
        <h3>Generate Invite Token</h3>
        <form className="admin-create-form" onSubmit={handleCreate}>
          <div className="admin-form-row">
            <div className="admin-form-field">
              <label>Token Type</label>
              <div className="token-type-toggle">
                <button
                  type="button"
                  className={`toggle-btn ${!grantsAdmin ? 'active' : ''}`}
                  onClick={() => setGrantsAdmin(false)}
                >
                  Regular User
                </button>
                {isSuperAdmin && (
                  <button
                    type="button"
                    className={`toggle-btn toggle-admin ${grantsAdmin ? 'active' : ''}`}
                    onClick={() => setGrantsAdmin(true)}
                  >
                    Admin
                  </button>
                )}
              </div>
            </div>
            <div className="admin-form-field">
              <label>Expires In (days)</label>
              <input
                type="number"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
                placeholder="Never"
                min="1"
                max="365"
              />
            </div>
            <div className="admin-form-field admin-form-submit">
              <button type="submit" className="btn btn-primary" disabled={creating}>
                {creating ? 'Generating...' : 'Generate Token'}
              </button>
            </div>
          </div>
        </form>

        {createdToken && (
          <div className="created-token-banner">
            <div className="created-token-label">New token created — share it with the user:</div>
            <div className="created-token-value">
              <code>{createdToken}</code>
              <button
                className="btn btn-sm"
                onClick={() => copyToClipboard(createdToken)}
              >
                {copied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Management */}
      <div className="admin-tokens-section">
        <h3>{isSuperAdmin ? 'All Users' : 'Users You Invited'}</h3>
        {loading ? (
          <div className="loading-spinner"><div className="spinner" /><span>Loading...</span></div>
        ) : users.length === 0 ? (
          <div className="empty-state">No users to manage.</div>
        ) : (
          <div className="table-wrapper">
            <table className="admin-token-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className={!u.is_active ? 'row-dimmed' : ''}>
                    <td style={{ fontWeight: 500 }}>{u.username}</td>
                    <td style={{ color: '#a0a0b8' }}>{u.email}</td>
                    <td>
                      <span className={`role-badge ${getUserRoleClass(u)}`}>
                        {getUserRole(u)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${u.is_active ? 'status-available' : 'status-expired'}`}>
                        {u.is_active ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="cell-date">{formatDate(u.created_at)}</td>
                    <td>
                      {!u.is_super_admin && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteUser(u.id, u.username)}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Token List */}
      <div className="admin-tokens-section">
        <h3>Invite Tokens</h3>
        {loading ? (
          <div className="loading-spinner"><div className="spinner" /><span>Loading tokens...</span></div>
        ) : invites.length === 0 ? (
          <div className="empty-state">No invite tokens have been created yet.</div>
        ) : (
          <div className="table-wrapper">
            <table className="admin-token-table">
              <thead>
                <tr>
                  <th>Token</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => {
                  const tokenStatus = getTokenStatus(inv);
                  return (
                    <tr key={inv.id} className={tokenStatus !== 'available' ? 'row-dimmed' : ''}>
                      <td className="cell-token">
                        <code>{inv.token.slice(0, 20)}...</code>
                        {tokenStatus === 'available' && (
                          <button
                            className="btn btn-sm btn-link"
                            onClick={() => copyToClipboard(inv.token)}
                            title="Copy full token"
                          >
                            Copy
                          </button>
                        )}
                      </td>
                      <td>
                        <span className={`role-badge ${getTokenRoleClass(inv)}`}>
                          {getTokenRole(inv)}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge status-${tokenStatus}`}>
                          {tokenStatus === 'available' ? 'Available' :
                           tokenStatus === 'used' ? 'Used' : 'Expired'}
                        </span>
                      </td>
                      <td className="cell-date">{formatDate(inv.created_at)}</td>
                      <td className="cell-date">{inv.expires_at ? formatDate(inv.expires_at) : 'Never'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
